/**
 * ## Message Bus
 *
 * The message bus is a central messaging channel. It is used for publishing system-wide events that any module can subscribe to.
 *
 * ### Examples
 *
 * #### Interface
 *
 *     import { messageBus as bus } from '@pbs/ngen-assets';
 *
 * #### Subscribe
 *
 *     var mySubscription = bus.subscribe('test', function (message) {
 *         console.log(message.data); // Log published data
 *     });
 *
 * #### Publish string data
 *
 *     bus.publish('test', 'Hello');
 *
 * #### Publish object data
 *
 *     bus.publish('test', { someValue: 2 });
 *
 * #### Stop listening to an event
 *
 *     mySubscription.dispose();
 *
 * @module module:modules/message-bus
 *
 * @public
 */
import { subscribable } from '../utils';

const channel = subscribable.mixin({});

export default {
    /**
     * Subscribes to an event.
     *
     * @example <caption>The callback will be called with a message object</caption>
     *
     * var mySubscription = bus.subscribe('test', function (message) {
     *     console.log(message.data); // Log published data
     * });
     *
     * @example <caption>Stop listening to an event</caption>
     *
     * mySubscription.dispose();
     *
     * @param {String} event Filter pattern to listen on (will be compiled into a regex).
     * @param {function} callback Function that will be called when a message matches the event.
     *
     * @return {Object} Subscription object
     *
     * @memberof module:modules/message-bus
     *
     * @instance
     *
     * @public
     */
    subscribe: (event, callback) => channel && channel.subscribe(event, callback),

    /**
     * Publishes an event with optional data.
     *
     * @param {String} event The full topic of the event.
     * @param {Object} data Data to send with the event (will be wrapped in a message object).
     *
     * @memberof module:modules/message-bus
     *
     * @instance
     *
     * @public
     */
    publish: (event, data) => {
        const message = {
            data: data,
            event: event,
        };
        channel.publish(event, message);
    },

    /**
     * Subscribes to an event and automatically disposes the subscription once it gets triggered.
     *
     * @example <caption>The callback will be called with a message object</caption>
     *
     * var mySubscription = bus.subscribeOnce('test', function (message) {
     *     console.log(message.data); // Log published data
     * });
     *
     * @example <caption>Stop listening to an event if it hasn't triggered</caption>
     *
     * mySubscription.dispose();
     *
     * @memberof module:modules/message-bus
     *
     * @param {string} event event name
     * @param {function} callback a callback
     *
     * @return {{}} subscription
     *
     * @instance
     *
     * @private
     */
    subscribeOnce: (event, callback) => {
        const subscription = channel.subscribe(event, callback);
        const cb = subscription.callback;
        subscription.callback = function (...args) {
            cb.apply(subscription, args);
            subscription.dispose.bind(subscription)();
        };

        return subscription;
    },

    /**
     * Retrieve an instrumentation object for debugging.
     *
     * @return {Object} Instrumentation object
     *
     * @private
     */
    instrumentation: () => ({
        __this__: true,
        __name__: 'Message bus',
        subscriptions: channel._subscriptions.map(s => s.event),
    }),
};
